import React, { useState, useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { LMSProvider, useLMS } from './context/LMSContext';
import { IdleTimeoutContext } from './context/IdleTimeoutContext';
import useIdleTimeout from './hooks/useIdleTimeout';
import LoginPage from './components/shared/LoginPage';
import SetPasswordScreen from './components/shared/SetPasswordScreen';
import ResetPasswordScreen from './components/shared/ResetPasswordScreen';
import Sidebar from './components/shared/Sidebar';
import AdminDashboard from './components/admin/AdminDashboard';
import StudentDashboard from './components/student/StudentDashboard';
import StudentProfile from './components/student/StudentProfile';
import CohortView from './components/shared/CohortView';
import CalendarView from './components/shared/CalendarView';
import SyllabusView from './components/shared/SyllabusView';
import ModuleView from './components/shared/ModuleView';
import Messages from './components/shared/Messages';
import UserManagement from './components/admin/UserManagement';
import MasterModules from './components/admin/MasterModules';
import StudentProgress from './components/admin/StudentProgress';
import AssignmentGrid from './components/admin/AssignmentGrid';
import AttendanceRoster from './components/admin/AttendanceRoster';
import DocumentsView from './components/shared/DocumentsView';
import AdminReflections from './components/admin/AdminReflections';
import ZoomSettings from './components/admin/ZoomSettings';
import ThinkificEnrollment from './components/admin/ThinkificEnrollment';

function AppContent() {
  const { isAuthenticated, isLoading, currentUser, currentView, isDarkMode, isPasswordRecovery, logout } = useLMS();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // =========================================================================
  // IDLE TIMEOUT - Auto-logout after 10 minutes of inactivity
  // Only active when user is authenticated
  // Shows a 60-second warning before logging out
  // =========================================================================
  const handleIdleLogout = useCallback(async () => {
    console.log('Idle timeout triggered - logging out');
    await logout();
  }, [logout]);

  const { showWarning, remainingSeconds, dismissWarning, suspend, resume } = useIdleTimeout({
    timeoutMinutes: 10,
    warningMinutes: 1,
    onLogout: handleIdleLogout,
    enabled: isAuthenticated && !!currentUser
  });

  // Handed to screens that run long jobs the user watches without touching
  // anything (the submissions PDF export), so the countdown does not sign them
  // out mid-job. suspend/resume are stable, so this value is too.
  const idleControls = useMemo(() => ({ suspend, resume }), [suspend, resume]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  const renderView = () => {
    if (!currentUser) return null;
    
    const isAdmin = currentUser.role === 'admin';
    const isProfessor = currentUser.role === 'professor';
    const isStaff = isAdmin || isProfessor;
    // Clerical role: limited admin-assistant who can manage roster, attendance,
    // password resets, and Zoom link settings. NOT part of isStaff so existing
    // staff-only edit paths (module/master-module editing) remain untouched.
    const isClerical = currentUser.role === 'clerical';
    const canViewRosterTools = isStaff || isClerical;

    switch (currentView) {
      case 'dashboard':
        return canViewRosterTools ? <AdminDashboard /> : <StudentDashboard />;
      case 'cohort':
        return <CohortView />;
      case 'calendar':
        return <CalendarView />;
      case 'syllabus':
        return <SyllabusView />;
      case 'module':
        return <ModuleView />;
      case 'profile':
        return <StudentProfile />;
      case 'messages':
        return <Messages />;
      case 'users':
        // Admin and Clerical can access user management.
        // Clerical is restricted inside UserManagement itself (students-only, no delete).
        return (isAdmin || isClerical)
          ? <UserManagement />
          : (isStaff ? <AdminDashboard /> : <StudentDashboard />);
      case 'master-modules':
        // Master Modules is strictly off-limits to clerical.
        return isStaff ? <MasterModules /> : (isClerical ? <AdminDashboard /> : <StudentDashboard />);
      case 'student-progress':
        return isStaff ? <StudentProgress /> : (isClerical ? <AdminDashboard /> : <StudentDashboard />);
      case 'assignments':
        return isStaff ? <AssignmentGrid /> : (isClerical ? <AdminDashboard /> : <StudentDashboard />);
      case 'attendance':
        return canViewRosterTools ? <AttendanceRoster /> : <StudentDashboard />;
      case 'reflections':
        return isStaff ? <AdminReflections /> : (isClerical ? <AdminDashboard /> : <StudentDashboard />);
      case 'documents':
        return <DocumentsView />;
      case 'zoom-settings':
        return canViewRosterTools ? <ZoomSettings /> : <StudentDashboard />;
      case 'thinkific':
        // Thinkific NCLEX course management is strictly admin-only.
        // Clerical/professor/student all fall back to their default view.
        return isAdmin
          ? <ThinkificEnrollment />
          : (canViewRosterTools ? <AdminDashboard /> : <StudentDashboard />);
      default:
        return canViewRosterTools ? <AdminDashboard /> : <StudentDashboard />;
    }
  };

  // Show loading spinner while checking for existing session OR while fetching user profile
  if (isLoading || (isAuthenticated && !currentUser)) {
    return (
      <div className={`app ${isDarkMode ? 'dark-mode' : ''}`}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #dc2626',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ color: '#6b7280' }}>Loading...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={`app ${isDarkMode ? 'dark-mode' : ''}`}>
        <LoginPage />
      </div>
    );
  }

  // Show password reset screen when user clicked "forgot password" link in email
  if (isPasswordRecovery) {
    return (
      <div className={`app ${isDarkMode ? 'dark-mode' : ''}`}>
        <ResetPasswordScreen />
      </div>
    );
  }

  // Force invited users (who haven't set a password) to set one before continuing
  if (currentUser && !currentUser.password_set && currentUser.role !== 'admin') {
    return (
      <div className={`app ${isDarkMode ? 'dark-mode' : ''}`}>
        <SetPasswordScreen />
      </div>
    );
  }

  return (
    <IdleTimeoutContext.Provider value={idleControls}>
    <div className={`app ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Idle Timeout Warning Modal */}
      {showWarning && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal" style={{ maxWidth: '420px', textAlign: 'center' }}>
            <div className="modal-header" style={{ justifyContent: 'center', borderBottom: 'none' }}>
              <h3 style={{ margin: 0 }}>Session Expiring</h3>
            </div>
            <div style={{ padding: '8px 24px 16px' }}>
              <div style={{
                fontSize: '48px',
                fontWeight: '700',
                color: remainingSeconds <= 15 ? '#dc2626' : '#f59e0b',
                margin: '8px 0 12px',
                fontVariantNumeric: 'tabular-nums'
              }}>
                {Math.floor(remainingSeconds / 60)}:{String(remainingSeconds % 60).padStart(2, '0')}
              </div>
              <p style={{ color: '#6b7280', margin: '0 0 20px', lineHeight: '1.5' }}>
                You'll be logged out due to inactivity. Click below to stay logged in.
              </p>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'center', borderTop: 'none', paddingTop: 0 }}>
              <button className="btn-primary" onClick={dismissWarning} style={{ minWidth: '180px' }}>
                I'm Still Here
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu Toggle */}
      <button className="mobile-menu-toggle" onClick={toggleSidebar}>
        {sidebarOpen ? '✕' : '☰'}
      </button>

      {/* Overlay for mobile */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`} 
        onClick={closeSidebar}
      />

      <div className="main-layout">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        <main className="main-content">
          <React.Fragment key={currentView}>
            {renderView()}
          </React.Fragment>
        </main>
      </div>
    </div>
    </IdleTimeoutContext.Provider>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<div style={{ padding: '40px', textAlign: 'center' }}>
      <h2>Something went wrong</h2>
      <p style={{ color: '#6b7280' }}>Please refresh the page or contact support if the issue persists.</p>
      <button onClick={() => window.location.reload()} style={{ marginTop: '16px', padding: '8px 20px', cursor: 'pointer' }}>
        Refresh Page
      </button>
    </div>}>
      <LMSProvider>
        <AppContent />
      </LMSProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
